import React, { useEffect, useState } from 'react'
import {
    Select,
    Button,
    DialogActions,
    FormControl,
    InputLabel,
    makeStyles,
} from '@material-ui/core'
import EditableDialogWrapper from './editableDialogWrapper'
import PropTypes from 'prop-types'

const useStyles = makeStyles(theme => ({
    spaced: {
        padding: theme.spacing(2),
    },
}))

const DropdownDialog = ({
    title,
    description,
    open,
    onConfirm,
    onReset,
    onClose,
    currentValue,
    dropdownItems,
    onChange,
    resetButtonVisible = true,
    confirmButtonText,
}) => {
    const [selectedValue, setSelectedValue] = useState(currentValue)
    const labelText = description?.trim() || 'Please select'
    const [announcement, setAnnouncement] = useState('')
    const classes = useStyles()

    useEffect(() => {
        if (!open || !dropdownItems.length) return

        const trimmedCurrent = currentValue?.trim()
        const match = dropdownItems.find(
            item => item.value.trim() === trimmedCurrent
        )

        if (match) {
            setSelectedValue(trimmedCurrent)
        } else {
            const fallback = dropdownItems[0].value.trim()
            setSelectedValue(fallback)
            onChange(fallback)
        }

        const timeout = setTimeout(() => {
            const optionsText = dropdownItems
                .map((item, index) => `Option ${index + 1}, ${item.value}`)
                .join(' - ')

            setAnnouncement(`Available options are: ${optionsText}`)
        }, 100)

        return () => clearTimeout(timeout)
    }, [open, dropdownItems, currentValue])

    return (
        <EditableDialogWrapper
            title={title}
            open={open}
            onClose={onClose}
            ariaLabel={`${title} dialog`}
        >
            <FormControl fullWidth className={classes.spaced}>
                <InputLabel
                    id='multi-select-input'
                    htmlFor='select-option'
                    className={classes.spaced}
                >
                    {labelText}
                </InputLabel>
                <Select
                    native
                    labelId='multi-select-input'
                    id='select-option'
                    displayEmpty
                    value={selectedValue}
                    onChange={e => {
                        setSelectedValue(e.target.value.trim())
                        onChange(e.target.value.trim())
                    }}
                >
                    {dropdownItems.map((item, index) => (
                        <option
                            key={`${item.value}-${index}`}
                            value={item.value.trim()}
                        >
                            {item.value}
                        </option>
                    ))}
                </Select>
            </FormControl>
            <DialogActions>
                {resetButtonVisible && (
                    <Button onClick={() => onReset(title)} color='secondary'>
                        Reset
                    </Button>
                )}
                <Button
                    variant='contained'
                    onClick={() =>
                        onConfirm(selectedValue === title ? '' : selectedValue)
                    }
                    color='secondary'
                >
                    {confirmButtonText || 'Confirm'}
                </Button>
            </DialogActions>
            {/* Hidden live region for screen readers */}
            <div
                aria-live='assertive'
                aria-atomic='true'
                style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                }}
            >
                {announcement}
            </div>
        </EditableDialogWrapper>
    )
}
DropdownDialog.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    open: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    dropdownItems: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
        })
    ).isRequired,
    onChange: PropTypes.func.isRequired,
    resetButtonVisible: PropTypes.bool,
    confirmButtonText: PropTypes.string,
}

export default DropdownDialog
